import {
  Article,
  ArticleResolution,
} from '@wix/answers-api';
import classNames from 'classnames';
import React, { useState } from 'react';

import { useWindowSize } from '../../hooks/useWindowSize';
import type { Step } from '../../pages/article/[articleTitle]';
import { ArticleWithComponents } from '../../types';
import { hasHideChatbotEpLabel } from '../../utils/article-labels';
import { useStore } from '../../zustand/stores';
import { CopilotDisplayMode } from '../../zustand/stores/copilot';
import { ArticleABTestNotification } from '../ArticleABTestNotification';
import { ArticlePreviewNotification } from '../ArticlePreviewNotification';
import { BreadcrumbItem } from '../Breadcrumbs';
import { ArticleLeftPanel } from './ArticleLeftPanel';
import { ArticleMainContent } from './ArticleMainContent';
import { ArticleRightPanel } from './ArticleRightPanel';

import css from './index.module.scss';

export type ArticlePageLayoutProps = {
  article: ArticleWithComponents;
  relatedArticles: Article[];
  steps: Step[];
  breadcrumbItems: BreadcrumbItem[];
  copilotArticleContentRef: React.RefObject<HTMLDivElement>;
  anchorsRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  selfArticleRedirect: (
    url: string,
    shallow?: boolean,
    targetLocale?: string
  ) => void;
  onClickStatus: (resolution: ArticleResolution) => Promise<void>;
  hasActiveLiveChatSession?: boolean;
};

enum OpenPanel {
  Left = 'left',
  Right = 'right',
  None = 'none',
}

export const ArticlePageLayout = ({
  article,
  relatedArticles,
  steps,
  breadcrumbItems,
  copilotArticleContentRef,
  anchorsRef,
  isLoading,
  selfArticleRedirect,
  onClickStatus,
  hasActiveLiveChatSession,
}: ArticlePageLayoutProps) => {
  const [openPanel, setOpenPanel] = useState<OpenPanel>(
    hasActiveLiveChatSession ? OpenPanel.Right : OpenPanel.Left
  );
  const isLeftPanelCollapsed = openPanel !== OpenPanel.Left;
  const isRightPanelCollapsed = openPanel !== OpenPanel.Right;
  const expand = (panel: OpenPanel) => () => setOpenPanel(panel);
  const collapse = (panel: OpenPanel) => () =>
    setOpenPanel(prev => (prev === panel ? OpenPanel.None : prev));
  const copilotDisplayMode = useStore(
    state => state.copilotSlice.copilotDisplayMode,
  );
  const windowSize = useWindowSize();
  const shouldHideLeftPanel =
    !!windowSize.width && windowSize.width <= 1215;
  const hasSteps = steps.length > 0;
  const showLeftPanel = hasSteps && !shouldHideLeftPanel;
  const isChatbotEpHidden = hasHideChatbotEpLabel(article);
  const hasRightPanel =
    !isChatbotEpHidden && (
      copilotDisplayMode === CopilotDisplayMode.Chatbot ||
      copilotDisplayMode === CopilotDisplayMode.Loading ||
      copilotDisplayMode === CopilotDisplayMode.UnhelpfulFeedback
    );
  const isRightPanelLoading = copilotDisplayMode === CopilotDisplayMode.Loading;

  return (
    <>
      <ArticlePreviewNotification />
      <ArticleABTestNotification article={article} />
      <div
        className={classNames(css.articlePageLayout, {
          [css.hasLeftPanel]: showLeftPanel,
          [css.hasRightPanel]: hasRightPanel,
        })}
      >
        {showLeftPanel && (
          <ArticleLeftPanel
            steps={steps}
            article={article}
            copilotArticleContentRef={copilotArticleContentRef}
            anchorsRef={anchorsRef}
            isCollapsed={isLeftPanelCollapsed || isLoading}
            isRightPanelLoading={isRightPanelLoading}
            onCollapse={collapse(OpenPanel.Left)}
            onExpand={expand(OpenPanel.Left)}
          />
        )}
        <ArticleMainContent
          article={article}
          relatedArticles={relatedArticles}
          steps={steps}
          breadcrumbItems={breadcrumbItems}
          copilotArticleContentRef={copilotArticleContentRef}
          isLoading={isLoading}
          selfArticleRedirect={selfArticleRedirect}
          onClickStatus={onClickStatus}
          onExpandRightPanel={expand(OpenPanel.Right)}
          isChatbotEpHidden={isChatbotEpHidden}
        />
        {!isChatbotEpHidden && (
          <ArticleRightPanel
            article={article}
            selfArticleRedirect={selfArticleRedirect}
            isCollapsed={isRightPanelCollapsed}
            onCollapse={collapse(OpenPanel.Right)}
            onExpand={expand(OpenPanel.Right)}
            suggestedQuestions={article.relatedQuestions as string[] | undefined}
          />
        )}
      </div>
    </>
  );
};
